<script setup lang="ts">
  import { ref, watch, onMounted, computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { MIN_DOCUMENT_TITLE_LENGTH } from '@/shared/config';
  import {
    ExternalDocumentUploadActions,
    ExternalDocumentUploadTips,
    DocumentUploadModeSelector,
    ExternalDocumentUrlUpload,
    ExternalDocumentInfo,
    DocumentDropzone,
    useDocumentUploadModal,
    useUrlUploadModal,
    useFileUploadStore,
    useUrlDataCollector,
    urlUploadDataStore,
  } from '@/features/Document';
  import { UiModalHeader, SelectInput } from '@/shared/ui';
  import type { DocumentPrefix, DocumentRevision } from '@/entities/Document';
  import { ThemeProvider } from '@/shared/ui';
  import { documentApi } from '@/entities/Document';
  import type { UrlInput } from '@/features/Document/model/upload/types';

  const props = defineProps<{
    title: string;
    isReplacement: boolean;
    documentId: number;
  }>();

  const emit = defineEmits<{
    (e: 'confirm'): void;
    (e: 'close', documentIds?: number[]): void;
    (e: 'upload-success'): void;
  }>();

  const prefix = ref();
  const prefixes = ref<{ id: number; name: string }[]>([]);
  const revision = ref();
  const revisions = ref<DocumentRevision[]>([]);
  const fileUploadStore = useFileUploadStore();

  const replacementContext = computed(() => {
    if (props.isReplacement && revision.value) {
      return { documentId: props.documentId, revisionId: revision.value };
    }

    return undefined;
  });

  const customEmit = {
    close: (documentIds?: number[]) => emit('close', documentIds),
    confirm: () => emit('confirm'),
    'upload-success': () => emit('upload-success'),
  };

  const collector = useUrlDataCollector();

  const collectExternalUrlData = () => {
    if (uploadMode.value !== 'url') return [];

    const component = urlUploadComponentRef.value;
    if (!component) return [];

    const formattedInputs = component.getFormattedInputs();
    if (!formattedInputs) return [];

    const validInputs = formattedInputs.filter((input: UrlInput) => input.isValid);
    const urlData: Array<{ url: string; name: string }> = [];

    for (const input of validInputs) {
      if (!input.url || !input.documentName) continue;

      const extension = urlUploadDataStore.getUrlExtension(input.url);
      const fileName = urlUploadDataStore.generateExternalFileName(
        input.documentName || documentName.value || fullDocumentName.value,
        extension
      );

      urlData.push({
        url: input.url,
        name: fileName,
      });
    }

    return urlData;
  };

  const {
    hasFiles,
    handleFileProgress,
    handleFileComplete,
    handleClose,
    handleConfirm,
    handleAllFilesDeleted,
    handleFileUpload,
    cleanState,
  } = useDocumentUploadModal(
    customEmit,
    () => replacementContext.value,
    () => collectExternalUrlData(),
    () => true,
    () => documentName.value || fullDocumentName.value
  );

  const getPrefixes = async () => {
    const responseData = await documentApi.getDocumentPrefixes();

    prefixes.value = responseData.map((prefix: DocumentPrefix) => ({
      id: prefix.id,
      name: prefix.code,
    }));
  };

  const getRevisions = async () => {
    const data = await documentApi.getDocumentById(props.documentId);

    if (data.revisions && data.revisions.length > 0) {
      revisions.value = data.revisions;
      revision.value = data.revisions[0].id;
    }
  };

  onMounted(() => {
    cleanState();
    urlUploadDataStore.clearAll();
    if (props.isReplacement) {
      getPrefixes();
      getRevisions();
    }
  });

  const documentName = ref('');

  let debounceTimer: NodeJS.Timeout | null = null;
  const fullDocumentName = ref('');

  const handleExternalFileUpload = (file: File[]) => {
    handleFileUpload(
      file,
      true,
      file.length === 1 ? documentName.value : ''
    );
  };

  const urlUploadComponentRef = ref<InstanceType<typeof ExternalDocumentUrlUpload>>();

  const areAllUrlsValid = computed(() => {
    const child = urlUploadComponentRef.value;
    if (!child) return false;
    return child.areAllUrlsValid ?? false;
  });

  const hasBatchDuplicateTitlesInUrlMode = computed(() => {
    const child = urlUploadComponentRef.value;
    if (!child || uploadMode.value !== 'url') return false;
    return (child.batchDuplicateNormalizedTitles?.size ?? 0) > 0;
  });

  const hasValidUrls = computed(() => {
    const child = urlUploadComponentRef.value;
    if (!child) return false;

    const hasValid = child.urlInputs ? child.urlInputs.some((input) => input.isValid) : false;

    return hasValid;
  });

  const modeSelectorKey = ref(0);

  const {
    uploadMode,
    handleModeChange: handleModeChangeOriginal,
    urlUploadInstances,
    addUrlUploadInstance,
    removeUrlUploadInstance: removeUrlUploadInstanceOriginal,
    handleUrlValidityChange,
    shouldConfirmModeChange,
    setInitialMode,
    setPendingMode,
    openModeChangeConfirmation,
    confirmModeChange,
  } = useUrlUploadModal({
    cleanState,
    handleAllFilesDeleted,
    isUrlUploadValid: areAllUrlsValid,
    hasFiles,
    onCancelModeChange: () => {
      modeSelectorKey.value += 1;
    },
  });

  const handleModeChange = (newMode: 'upload' | 'url' | undefined) => {
    if (shouldConfirmModeChange(newMode, hasFiles.value, hasValidUrls.value)) {
      setPendingMode(newMode);
      openModeChangeConfirmation();
    } else {
      setInitialMode(newMode);
      if (urlUploadComponentRef.value) {
        urlUploadComponentRef.value.resetUrlInputs();
      }
    }
  };

  watch(uploadMode, (newMode, oldMode) => {
    if (oldMode === 'url' && newMode !== 'url' && urlUploadComponentRef.value) {
      urlUploadComponentRef.value.resetUrlInputs();
    }
  });

  const removeUrlUploadInstance = (index: number) => {
    removeUrlUploadInstanceOriginal(index);
  };

  watch(documentName, (name: string) => {
    fileUploadStore.uploadDuplicateTitles = [];

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (name && name.trim() && name.length >= MIN_DOCUMENT_TITLE_LENGTH) {
      debounceTimer = setTimeout(() => {
        fullDocumentName.value = name;
        fileUploadStore.externalDocumentName = name;
      }, 300);
    } else {
      fullDocumentName.value = '';
      fileUploadStore.externalDocumentName = '';
    }
  });

  watch(
    () => urlUploadComponentRef.value?.urlInputs,
    () => {
      fileUploadStore.isUrlUploadValid = areAllUrlsValid.value;
    },
    { deep: true, immediate: true }
  );
</script>

<template>
  <VueFinalModal
    :click-to-close="false"
    :esc-to-close="false"
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-3/4 divide-y divide-gray-02"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="overflow-auto">
      <UiModalHeader
        :title="title"
        text-color="gray-07"
        class="px-6 py-4 text-sm border-b border-border-color"
        @close="handleClose"
      />
      <div
        id="modal-content"
        class="flex flex-col gap-5 max-h-[80vh] overflow-y-auto"
        :class="fileUploadStore.filesLoaded ? 'px-3 py-6' : 'px-8 py-6'"
      >
        <ExternalDocumentUploadTips
          v-if="!isReplacement"
          class="mb-6"
        />
        <DocumentUploadModeSelector
          v-if="!isReplacement"
          :key="modeSelectorKey"
          :model-value="uploadMode"
          @update:model-value="handleModeChange"
        />
        <div class="flex flex-col gap-2">
          <DocumentDropzone
            v-if="(!isReplacement && uploadMode === 'upload') || (prefix && revision)"
            :files-loaded="fileUploadStore.filesLoaded"
            :multiple="!isReplacement"
            :replacement-context="replacementContext"
            :is-external="!isReplacement"
            :document-name="documentName"
            @files-selected="handleExternalFileUpload"
            @upload-progress="handleFileProgress"
            @upload-complete="
              (_fileId: string, documentIds?: number[]) => handleFileComplete(documentIds)
            "
            @all-files-deleted="handleAllFilesDeleted"
          />
          <div
            v-if="uploadMode === 'url'"
            class="flex flex-col gap-4"
          >
            <ExternalDocumentUrlUpload
              ref="urlUploadComponentRef"
              :duplicate-titles-from-backend="fileUploadStore.linkDuplicateTitles"
              @clear-duplicate-titles="(fileUploadStore.linkDuplicateTitles = [])"
            />
          </div>
          <p
            v-if="
              !isReplacement &&
                ((uploadMode === 'upload' &&
                  (fileUploadStore.uploadBatchDuplicateTitles?.length ?? 0) > 0) ||
                  hasBatchDuplicateTitlesInUrlMode)
            "
            class="text-sm text-red-16 -mt-4"
            role="alert"
          >
            Existem títulos duplicados no lote.
          </p>
        </div>
        <div>
          <div
            v-if="isReplacement"
            class="flex gap-6"
          >
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-gray-07">Prefixo:</span>
              <SelectInput
                v-model="prefix"
                :options="prefixes"
                label="Selecione o prefixo"
                option-label="code"
                option-value="id"
              />
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-gray-07">Revisão:</span>
              <SelectInput
                v-model="revision"
                :options="
                  revisions.map((rev: DocumentRevision) => ({
                    id: rev.id,
                    name: rev.revision.toString(),
                  }))
                "
                label="Selecione a revisão a ser substituída"
              />
            </div>
          </div>
        </div>
        <ExternalDocumentUploadActions
          :has-files="hasFiles"
          :all-uploads-complete="
            (uploadMode === 'upload' &&
              fileUploadStore.filesLoaded &&
              (isReplacement || fileUploadStore.canSubmitExternalUpload)) ||
              (uploadMode === 'url' && areAllUrlsValid)
          "
          is-external
          class="!px-6 !py-8"
          @close="handleClose"
          @confirm="handleConfirm"
        />
        <ExternalDocumentInfo v-if="!isReplacement" />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
